// Flowing gold ribbons — the connectors between screens. Each is a smooth
// sine-wave current (solid, slightly transparent, with a soft glow underlay)
// whose wave travels outward along the line. The wave envelope is sin(πs), so
// both endpoints stay exactly on the base line and meet across screen pans.
// One light rAF ticker (≤30fps, ~5s loop) per <FlowRibbon>, paused when inactive.
import React, { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useV10 } from './theme';

export type Pt = { x: number; y: number };
const LOOP_MS = 5000;
/** [strokeWidth, opacity] layers of the stream, outside → core. */
const STREAM: [number, number][] = [
  [26, 0.018],
  [18, 0.026],
  [12, 0.036],
  [7, 0.048],
  [3.5, 0.06],
];

/** n+1 evenly spaced points on a straight line a→b (a = the orb end). */
export function straight(a: Pt, b: Pt, n = 40): Pt[] {
  return Array.from({ length: n + 1 }, (_, i) => ({ x: a.x + ((b.x - a.x) * i) / n, y: a.y + ((b.y - a.y) * i) / n }));
}
/** Samples of a cubic Bézier. */
export function cubic(p0: Pt, p1: Pt, p2: Pt, p3: Pt, n = 40): Pt[] {
  return Array.from({ length: n + 1 }, (_, i) => {
    const s = i / n;
    const u = 1 - s;
    const w = [u * u * u, 3 * u * u * s, 3 * u * s * s, s * s * s];
    return { x: w[0] * p0.x + w[1] * p1.x + w[2] * p2.x + w[3] * p3.x, y: w[0] * p0.y + w[1] * p1.y + w[2] * p2.y + w[3] * p3.y };
  });
}

function wavePath(pts: Pt[], phase: number, amp: number, cycles: number): string {
  const n = pts.length - 1;
  let d = '';
  for (let i = 0; i <= n; i++) {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(n, i + 1)];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const s = i / n;
    const off = amp * Math.sin(Math.PI * s) * Math.sin(2 * Math.PI * (cycles * s - phase));
    d += `${i === 0 ? 'M' : 'L'}${(pts[i].x - (dy / len) * off).toFixed(1)},${(pts[i].y + (dx / len) * off).toFixed(1)} `;
  }
  return d;
}

/** Lines are given orb-end first, so the wave flows outward as phase rises. */
export function FlowRibbon({
  lines,
  width,
  height,
  active = true,
  amp = 6,
  color,
}: {
  lines: Pt[][];
  width: number;
  height: number;
  active?: boolean;
  amp?: number;
  color?: string;
}) {
  const t = useV10();
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    let last = 0;
    const tick = (now: number) => {
      if (now - last >= 33) {
        last = now;
        setPhase((now % LOOP_MS) / LOOP_MS);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  const c = color ?? t.goldBright;
  return (
    <Svg pointerEvents="none" width={width} height={height} style={StyleSheet.absoluteFill}>
      {lines.map((pts, i) => {
        let len = 0;
        for (let k = 1; k < pts.length; k++) len += Math.hypot(pts[k].x - pts[k - 1].x, pts[k].y - pts[k - 1].y);
        const d = wavePath(pts, phase, amp, Math.max(1, len / 120));
        // A soft stream: stacked strokes, widest faintest, so the band fades
        // out at its sides; motes drift outward along the core.
        return (
          <React.Fragment key={i}>
            {STREAM.map(([w, o]) => (
              <Path key={w} d={d} stroke={c} strokeWidth={w} strokeOpacity={o} fill="none" strokeLinecap="round" />
            ))}
            <Path d={d} stroke={c} strokeWidth={2.4} strokeOpacity={0.16} strokeDasharray="0.1 14" strokeDashoffset={-phase * 14 * 6} fill="none" strokeLinecap="round" />
          </React.Fragment>
        );
      })}
    </Svg>
  );
}
